import { Button, Upload, notification } from "antd";
import { get } from "lodash";
import { BiDownload, BiUpload } from "react-icons/bi";
import { useMutation } from "shared/hooks/useApi";
import graphql from "utils/api/graphql";
import cuid from "cuid";
import * as XLSX from "xlsx";

const DEFAULT_AGE_BRACKETS = [
  "0-17", "18-25", "26-35", "36-45", "46-55", "56-65", "66+",
];

const parseExcel = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const workbook = XLSX.read(e.target.result, { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet);
      resolve(rows);
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });

const ExcelUpload = ({ coverages, zones, ageBrackets, setFieldValue, refetch }) => {
  const brackets = ageBrackets && ageBrackets.length > 0
    ? ageBrackets.map((b) => b.name)
    : DEFAULT_AGE_BRACKETS;
  const [createBenefit] = useMutation(graphql.mutations.CREATE_COVERAGE_BENEFIT);
  const [addMapping] = useMutation(graphql.mutations.CREATE_COVERAGE_MAPPING);
  const [createPricer] = useMutation(graphql.mutations.CREATE_PRICER);
  const [updatePricer] = useMutation(graphql.mutations.UPDATE_PRICER);
  const [updateMapping] = useMutation(graphql.mutations.UPDATE_COVERAGE_MAPPING);

  const findCoverage = (name) =>
    coverages.find(
      (c) =>
        get(c, "coverageCategory.label", "").toLowerCase() ===
          name.toLowerCase() ||
        get(c, "coverageCategory.value", "").toLowerCase() ===
          name.toLowerCase(),
    );

  const handleUploadBenefits = async (file) => {
    try {
      const rows = await parseExcel(file);
      let added = 0;
      let skipped = 0;
      let skippedDuplicates = 0;
      let updatedCount = 0;
      const skippedCoverages = new Set();
      const newMappingsByCoverage = {};
      const updatedMappings = {};

      for (const row of rows) {
        const coverageName = row.Coverage || row.coverage || "";
        const coverage = findCoverage(coverageName);
        if (!coverage) {
          skipped++;
          skippedCoverages.add(coverageName);
          continue;
        }

        const rowType = (row["Benefit Type"] || row.benefit_type || "").toLowerCase();
        const rowSubtype = (row.Subtype || row.subtype || "").toLowerCase();
        const existingMappings = get(coverage, "coverageMappings") || [];
        const alreadyAdded = (newMappingsByCoverage[coverage.id] || []);

        const existingMatch = [...existingMappings, ...alreadyAdded].find((m) => {
          const b = get(m, "coverage", {});
          return (
            (b.type || "").toLowerCase() === rowType &&
            (b.subtype || "").toLowerCase() === rowSubtype
          );
        });

        if (existingMatch) {
          const waitingDays = parseInt(row["Waiting Days"] || row.waiting_days || 0, 10);
          const brssPercentage = parseFloat(row["BRSS %"] || row.brss) || null;
          const pmssPercentage = parseFloat(row["PMSS %"] || row.pmss) || null;
          const carenceDays = parseInt(row["Carence (days)"] || row.carence || 0, 10) || null;
          const franchiseAmount = parseFloat(row["Franchise (€)"] || row.franchise || 0) || null;
          const pricingType = (
            row["Pricing Type"] || row.pricing_type || "flat"
          ).toLowerCase();

          try {
            await updateMapping({
              variables: {
                where: { id: existingMatch.id },
                data: { waitingDays, brssPercentage, pmssPercentage, carenceDays, franchiseAmount },
              },
            });

            let pricerData;
            const existingPricerId = get(existingMatch, "pricer.id");
            if (existingPricerId) {
              const newPrices = { ...get(existingMatch, "pricer.prices", {}), type: pricingType };
              await updatePricer({
                variables: {
                  where: { id: existingPricerId },
                  data: { prices: newPrices },
                },
              });
              pricerData = { ...get(existingMatch, "pricer"), prices: newPrices };
            } else {
              const pricerId = cuid();
              await createPricer({
                variables: {
                  data: { id: pricerId, type: "db", name: "auto", prices: { type: pricingType } },
                },
              });
              await updateMapping({
                variables: {
                  where: { id: existingMatch.id },
                  data: { pricer: { id: pricerId } },
                },
              });
              pricerData = { id: pricerId, type: "db", prices: { type: pricingType } };
            }

            updatedMappings[existingMatch.id] = {
              waitingDays,
              pricer: pricerData,
            };
          } catch (err) {
            // silent continue
          }

          updatedCount++;
          skipped++;
          continue;
        }

        const benefitId = cuid();
        const mappingId = cuid();
        const benefitData = {
          id: benefitId,
          type: row["Benefit Type"] || row.benefit_type || "",
          subtype: row.Subtype || row.subtype || null,
          order: parseInt(row.Order || row.order, 10) || 1,
        };

        const waitingDays = parseInt(row["Waiting Days"] || row.waiting_days || 0, 10);
        const brssPercentage = parseFloat(row["BRSS %"] || row.brss) || null;
        const pmssPercentage = parseFloat(row["PMSS %"] || row.pmss) || null;
        const carenceDays = parseInt(row["Carence (days)"] || row.carence || 0, 10) || null;
        const franchiseAmount = parseFloat(row["Franchise (€)"] || row.franchise || 0) || null;
        const pricingType = (
          row["Pricing Type"] || row.pricing_type || "flat"
        ).toLowerCase();

        const pricerId = cuid();
        await createBenefit({ variables: { data: benefitData } });
        await createPricer({
          variables: {
            data: {
              id: pricerId,
              type: "db",
              name: "auto",
              prices: { type: pricingType },
            },
          },
        });
        await addMapping({
          variables: {
            data: {
              id: mappingId,
              waitingDays,
              brssPercentage,
              pmssPercentage,
              carenceDays,
              franchiseAmount,
              insurancePlanCoverage: { id: coverage.id },
              coverage: { id: benefitId },
              pricer: { id: pricerId },
            },
          },
        });

        const newMapping = {
          id: mappingId,
          waitingDays,
          coverage: benefitData,
          limits: [],
          supportingInformation: [],
          pricer: { id: pricerId, type: "db", prices: { type: pricingType } },
        };

        if (!newMappingsByCoverage[coverage.id]) {
          newMappingsByCoverage[coverage.id] = [];
        }
        newMappingsByCoverage[coverage.id].push(newMapping);

        added++;
      }

      if (refetch) await refetch();

      const skippedReasons = [];
      if (skippedCoverages.size > 0) {
        skippedReasons.push(
          `Garanties inconnues : ${[...skippedCoverages].join(", ")}`,
        );
      }
      if (skippedDuplicates > 0) {
        skippedReasons.push(`${skippedDuplicates} doublon(s)`);
      }

      notification.success({
        message: `Prestations : ${added} ajoutée(s), ${updatedCount} mise(s) à jour, ${skipped - updatedCount} ignorée(s)`,
        description: skippedReasons.length > 0
          ? skippedReasons.join(". ")
          : undefined,
        duration: skippedReasons.length > 0 ? 8 : 4,
      });
    } catch (err) {
      notification.error({ message: `Échec de l'import : ${err.message}` });
    }

    return false;
  };

  const handleDownloadPricingTemplate = () => {
    const rows = [];

    for (const coverage of coverages) {
      const categoryName =
        get(coverage, "coverageCategory.label") ||
        get(coverage, "coverageCategory.value") ||
        "";
      const mappings = get(coverage, "coverageMappings") || [];

      for (const mapping of mappings) {
        const benefit = get(mapping, "coverage", {});
        const pricingType = get(mapping, "pricer.prices.type", "flat");

        if (pricingType === "flat") {
          rows.push({
            Coverage: categoryName,
            "Benefit Type": benefit.type || "",
            Subtype: benefit.subtype || "",
            "Pricing Type": "flat",
            Age: "",
            Zone: "",
            Price: "",
          });
        } else if (pricingType === "age") {
          for (const age of brackets) {
            rows.push({
              Coverage: categoryName,
              "Benefit Type": benefit.type || "",
              Subtype: benefit.subtype || "",
              "Pricing Type": "age",
              Age: age,
              Zone: "",
              Price: "",
            });
          }
        } else if (pricingType === "age_zone") {
          const zoneList = zones && zones.length > 0
            ? zones
            : [{ name: "Default" }];
          for (const age of brackets) {
            for (const zone of zoneList) {
              rows.push({
                Coverage: categoryName,
                "Benefit Type": benefit.type || "",
                Subtype: benefit.subtype || "",
                "Pricing Type": "age_zone",
                Age: age,
                Zone: zone.name,
                Price: "",
              });
            }
          }
        }
      }
    }

    if (rows.length === 0) {
      notification.warning({ message: "Aucune prestation pour générer le modèle" });
      return;
    }

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Pricing");

    const colWidths = [
      { wch: 15 }, { wch: 18 }, { wch: 15 },
      { wch: 14 }, { wch: 8 }, { wch: 12 }, { wch: 10 },
    ];
    ws["!cols"] = colWidths;

    XLSX.writeFile(wb, "pricing-template.xlsx");
    notification.success({ message: "Modèle téléchargé" });
  };

  const handleUploadPrices = async (file) => {
    try {
      const rows = await parseExcel(file);
      const benefitPrices = {};

      for (const row of rows) {
        const key = `${row.Coverage}|${row["Benefit Type"]}|${row.Subtype || ""}`;
        if (!benefitPrices[key]) {
          benefitPrices[key] = {
            coverage: row.Coverage,
            benefitType: row["Benefit Type"],
            subtype: row.Subtype || "",
            pricingType: (row["Pricing Type"] || "flat").toLowerCase(),
            prices: {},
          };
        }

        const entry = benefitPrices[key];
        const price = parseFloat(row.Price);
        if (isNaN(price)) continue;

        if (entry.pricingType === "flat") {
          entry.prices = { type: "flat", monthly: price };
        } else if (entry.pricingType === "age") {
          if (!entry.prices.grid) {
            entry.prices = { type: "age", grid: {} };
          }
          entry.prices.grid[row.Age] = price;
        } else if (entry.pricingType === "age_zone") {
          if (!entry.prices.grid) {
            entry.prices = { type: "age_zone", grid: {} };
          }
          const age = row.Age;
          const zoneName = row.Zone;
          const zone = (zones || []).find((z) => z.name === zoneName);
          const zoneKey = zone ? zone.id : zoneName;
          if (!entry.prices.grid[age]) {
            entry.prices.grid[age] = {};
          }
          entry.prices.grid[age][zoneKey] = price;
        }
      }

      let updated = 0;

      for (const entry of Object.values(benefitPrices)) {
        const coverage = findCoverage(entry.coverage);
        if (!coverage) continue;

        const mappings = get(coverage, "coverageMappings") || [];
        const mapping = mappings.find((m) => {
          const b = get(m, "coverage", {});
          return (
            (b.type || "").toLowerCase() === (entry.benefitType || "").toLowerCase() &&
            (b.subtype || "").toLowerCase() === (entry.subtype || "").toLowerCase()
          );
        });

        if (!mapping) continue;

        const existingPricerId = get(mapping, "pricer.id");
        let pricerId;

        if (existingPricerId) {
          await updatePricer({
            variables: {
              where: { id: existingPricerId },
              data: { prices: entry.prices },
            },
          });
          pricerId = existingPricerId;
        } else {
          pricerId = cuid();
          await createPricer({
            variables: {
              data: { id: pricerId, type: "db", name: "auto", prices: entry.prices },
            },
          });
        }

        await updateMapping({
          variables: {
            where: { id: mapping.id },
            data: { pricer: { id: pricerId } },
          },
        });

        updated++;
      }

      if (refetch) await refetch();

      notification.success({ message: `Tarifs mis à jour pour ${updated} prestation(s)` });
    } catch (err) {
      notification.error({ message: `Échec de l'import : ${err.message}` });
    }

    return false;
  };

  const handleDownloadBenefitsTemplate = () => {
    const coverageNames = coverages.map(
      (c) =>
        get(c, "coverageCategory.label") ||
        get(c, "coverageCategory.value") ||
        "",
    );

    const rows = coverageNames.length > 0
      ? coverageNames.map((name) => ({
          Coverage: name,
          "Benefit Type": "",
          Subtype: "",
          "Waiting Days": "",
          "BRSS %": "",
          "PMSS %": "",
          "Carence (days)": "",
          "Franchise (€)": "",
          "Pricing Type": "flat",
        }))
      : [
          {
            Coverage: "",
            "Benefit Type": "",
            Subtype: "",
            "Waiting Days": "",
            "BRSS %": "",
            "PMSS %": "",
            "Carence (days)": "",
            "Franchise (€)": "",
            "Pricing Type": "flat",
          },
        ];

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Benefits");

    ws["!cols"] = [
      { wch: 15 }, { wch: 18 }, { wch: 15 }, { wch: 14 }, { wch: 14 },
    ];

    XLSX.writeFile(wb, "benefits-template.xlsx");
    notification.success({ message: "Modèle prestations téléchargé" });
  };

  return (
    <div className="excel-actions">
      <Button
        icon={<BiDownload size={16} />}
        onClick={handleDownloadBenefitsTemplate}
      >
        Modèle prestations
      </Button>
      <Upload
        accept=".xlsx,.xls"
        showUploadList={false}
        beforeUpload={handleUploadBenefits}
      >
        <Button icon={<BiUpload size={16} />}>Importer prestations</Button>
      </Upload>
      <Button
        icon={<BiDownload size={16} />}
        onClick={handleDownloadPricingTemplate}
        disabled={
          !coverages.some(
            (c) => (get(c, "coverageMappings") || []).length > 0,
          )
        }
      >
        Modèle tarification
      </Button>
      <Upload
        accept=".xlsx,.xls"
        showUploadList={false}
        beforeUpload={handleUploadPrices}
      >
        <Button
          icon={<BiUpload size={16} />}
          disabled={
            !coverages.some(
              (c) => (get(c, "coverageMappings") || []).length > 0,
            )
          }
        >
          Importer tarifs
        </Button>
      </Upload>
    </div>
  );
};

export default ExcelUpload;
